import '../../App.css';
import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';
import PostedPost from '../components/PostedPost';
import { PlatformIcon } from '../../components/general/AccountIcons';
import myImage from '../../assets/empty-box-2.png';

/* ── network definitions ─────────────────────────────────────────────────── */
const NETWORKS = [
    { id: '',              label: 'All',       color: '#475569', bg: '#f1f5f9' },
    { id: 'instagram',     label: 'Instagram', color: '#e1306c', bg: '#fdf2f8' },
    { id: 'linkedin',      label: 'LinkedIn',  color: '#0a66c2', bg: '#eff6ff' },
    { id: 'linkedin_page', label: 'LinkedIn Page', color: '#0a66c2', bg: '#eff6ff' },
    { id: 'twitter',       label: 'X / Twitter', color: '#000',  bg: '#f8fafc' },
    { id: 'facebook',      label: 'Facebook',  color: '#1877f2', bg: '#eff6ff' },
    { id: 'tiktok',        label: 'TikTok',    color: '#010101', bg: '#f8fafc' },
    { id: 'threads',       label: 'Threads',   color: '#000',    bg: '#f8fafc' },
    { id: 'youtube',       label: 'YouTube',   color: '#ff0000', bg: '#fff5f5' },
];

const PER_PAGE = 20;

/* ── styles ──────────────────────────────────────────────────────────────── */
const s = {
    header: { marginBottom: 24 },
    title:  { fontSize: '1.45rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 },
    subtitle: { fontSize: '0.9rem', color: '#64748b', margin: 0 },

    filtersRow: {
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14,
    },
    networkBtn: (active, color, bg) => ({
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 13px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600,
        cursor: 'pointer', border: `1.5px solid ${active ? color : '#e2e8f0'}`,
        background: active ? bg : '#fff', color: active ? color : '#64748b',
        transition: 'all 0.12s',
    }),
    dot: (color) => ({
        width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
    }),


    dateRow: {
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 20,
    },
    dateLabel: { fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600 },
    dateInput: {
        border: '1px solid #d1d9e0', borderRadius: 8, padding: '6px 10px',
        fontSize: '0.85rem', color: '#1e293b', outline: 'none', background: '#fff',
    },
    clearBtn: {
        background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
        fontSize: '0.8rem', padding: '4px 8px', borderRadius: 6,
        display: 'flex', alignItems: 'center', gap: 4,
    },

    pagination: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, marginTop: 32,
    },
    pageBtn: (active, disabled) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, borderRadius: 8,
        border: active ? 'none' : '1px solid #e2e8f0',
        background: active ? '#2563eb' : disabled ? '#f8fafc' : '#fff',
        color: active ? '#fff' : disabled ? '#cbd5e1' : '#475569',
        fontWeight: 600, fontSize: '0.85rem', cursor: disabled ? 'default' : 'pointer',
    }),
    pageInfo: { fontSize: '0.85rem', color: '#64748b', padding: '0 4px' },

    viewToggle: {
        display: 'flex', gap: 4, marginLeft: 'auto',
    },
    viewBtn: (active) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 7, cursor: 'pointer',
        border: active ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
        background: active ? '#eff6ff' : '#fff',
        color: active ? '#2563eb' : '#94a3b8',
        transition: 'all 0.12s',
    }),

    emptyState: {
        textAlign: 'center', padding: '60px 24px',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
    },
};

/* ── component ───────────────────────────────────────────────────────────── */
function PostedItems() {
    const [posts, setPosts]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [page, setPage]             = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal]           = useState(0);
    const [network, setNetwork]       = useState('');
    const [dateFrom, setDateFrom]     = useState('');
    const [dateTo, setDateTo]         = useState('');
    const [viewMode, setViewMode]     = useState('card');

    const fetchPosts = useCallback(async (pg, net, from, to) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pg, per_page: PER_PAGE });
            if (net)  params.set('network',   net);
            if (from) params.set('date_from', from);
            if (to)   params.set('date_to',   to);

            const res  = await fetch(
                `https://api.schedpilot.com/wp-json/api/v1/posted-items?${params}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            setPosts(data.posts      ?? []);
            setTotal(data.total      ?? 0);
            setTotalPages(data.total_pages ?? 1);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchPosts(page, network, dateFrom, dateTo);
    }, [page, network, dateFrom, dateTo, fetchPosts]);

    const handleNetwork = (id) => {
        setNetwork(id);
        setPage(1);
    };

    const handleDateFrom = (e) => { setDateFrom(e.target.value); setPage(1); };
    const handleDateTo   = (e) => { setDateTo(e.target.value);   setPage(1); };
    const clearDates     = ()  => { setDateFrom(''); setDateTo(''); setPage(1); };

    const pageNumbers = () => {
        const pages = [];
        const delta = 2;
        for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <MainStructure activeItem="posted-items">
            <div>

                <div style={s.header}>
                    <h1 style={s.title}>Published Posts</h1>
                    <p style={s.subtitle}>
                        {total > 0 ? `${total} published post${total !== 1 ? 's' : ''}` : 'All your published posts'}
                    </p>
                </div>

                {/* Network filter */}
                <div style={s.filtersRow}>
                    {NETWORKS.map(n => (
                        <button
                            key={n.id}
                            style={s.networkBtn(network === n.id, n.color, n.bg)}
                            onClick={() => handleNetwork(n.id)}
                        >
                            {n.id
                                ? <PlatformIcon type={n.id} size={14} />
                                : <Icon.Grid size={13} color={network === '' ? n.color : '#94a3b8'} />}
                            {n.label}
                        </button>
                    ))}
                </div>

                {/* Date filter */}
                <div style={s.dateRow}>
                    <span style={s.dateLabel}>Date range</span>
                    <input
                        type="date"
                        style={s.dateInput}
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={handleDateFrom}
                    />
                    <span style={s.dateLabel}>to</span>
                    <input
                        type="date"
                        style={s.dateInput}
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={handleDateTo}
                    />
                    {(dateFrom || dateTo) && (
                        <button style={s.clearBtn} onClick={clearDates}>
                            <Icon.X size={13} /> Clear
                        </button>
                    )}
                    <div style={s.viewToggle}>
                        <button style={s.viewBtn(viewMode === 'card')} onClick={() => setViewMode('card')} title="Card view">
                            <Icon.Grid size={15} />
                        </button>
                        <button style={s.viewBtn(viewMode === 'list')} onClick={() => setViewMode('list')} title="List view">
                            <Icon.List size={15} />
                        </button>
                    </div>
                </div>

                {/* Posts grid */}
                {loading ? (
                    <LoadingSpinner />
                ) : posts.length === 0 ? (
                    <div style={s.emptyState}>
                        <img src={myImage} alt="" style={{ width: 80, opacity: 0.4, marginBottom: 16 }} />
                        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                            No published posts found
                            {network ? ` for ${NETWORKS.find(n => n.id === network)?.label}` : ''}
                            {(dateFrom || dateTo) ? ' in that date range' : ''}.
                        </p>
                    </div>
                ) : (
                    <div className="row">
                        {posts.map(item => (
                            <PostedPost key={item.pid} myItem={item} viewMode={viewMode} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={s.pagination}>
                        <button
                            style={s.pageBtn(false, page === 1)}
                            disabled={page === 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            <Icon.ChevronLeft size={16} />
                        </button>

                        {page > 3 && (
                            <>
                                <button style={s.pageBtn(false, false)} onClick={() => setPage(1)}>1</button>
                                {page > 4 && <span style={s.pageInfo}>…</span>}
                            </>
                        )}

                        {pageNumbers().map(n => (
                            <button
                                key={n}
                                style={s.pageBtn(n === page, false)}
                                onClick={() => setPage(n)}
                            >
                                {n}
                            </button>
                        ))}

                        {page < totalPages - 2 && (
                            <>
                                {page < totalPages - 3 && <span style={s.pageInfo}>…</span>}
                                <button style={s.pageBtn(false, false)} onClick={() => setPage(totalPages)}>{totalPages}</button>
                            </>
                        )}

                        <button
                            style={s.pageBtn(false, page === totalPages)}
                            disabled={page === totalPages}
                            onClick={() => setPage(p => p + 1)}
                        >
                            <Icon.ChevronRight size={16} />
                        </button>

                        <span style={s.pageInfo}>Page {page} of {totalPages}</span>
                    </div>
                )}

            </div>
        </MainStructure>
    );
}

export default PostedItems;
